import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Flag, Clock, CheckCircle2 } from "lucide-react";
import { QuizData, UserAnswer } from "@/types/quiz";
import { useToast } from "@/hooks/use-toast";

const Quiz = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const quizData = location.state?.quizData as QuizData | undefined;
  const isShared = location.state?.isShared as boolean | undefined;
  const shareToken = location.state?.shareToken as string | undefined;
  const guestName = location.state?.guestName as string | undefined;

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<UserAnswer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!quizData) {
      toast({
        title: "No quiz data",
        description: "Please generate a quiz first",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    // Initialize answers array
    const initialAnswers: UserAnswer[] = quizData.questions.map((q) => ({
      questionId: q.id,
      selectedOption: null,
      markedForReview: false,
      timeSpent: 0,
    }));
    setAnswers(initialAnswers);

    // Set timer
    setTimeRemaining(quizData.settings.timerMinutes * 60);
  }, [quizData, navigate, toast]);

  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  if (!quizData) return null;

  const currentQ = quizData.questions[currentQuestion];
  const currentAnswer = answers[currentQuestion];
  const progress = ((currentQuestion + 1) / quizData.questions.length) * 100;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOptionSelect = (optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = {
      ...newAnswers[currentQuestion],
      selectedOption: optionIndex,
    };
    setAnswers(newAnswers);
  };

  const handleMarkForReview = () => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = {
      ...newAnswers[currentQuestion],
      markedForReview: !newAnswers[currentQuestion].markedForReview,
    };
    setAnswers(newAnswers);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    
    // Calculate results
    let correctAnswers = 0;
    let wrongAnswers = 0;
    let unanswered = 0;
    
    const detailedAnswers = answers.map((answer, index) => {
      const question = quizData.questions[index];
      const isCorrect = answer.selectedOption === question.correct_answer;
      
      if (answer.selectedOption === null) {
        unanswered++;
      } else if (isCorrect) {
        correctAnswers++;
      } else {
        wrongAnswers++;
      }
      
      return {
        ...answer,
        isCorrect,
        correctAnswer: question.correct_answer,
      };
    });

    const score = quizData.settings.negativeMarking
      ? correctAnswers + (wrongAnswers * quizData.settings.penalty)
      : correctAnswers;

    const timeTaken = (quizData.settings.timerMinutes * 60) - timeRemaining;

    navigate("/results", {
      state: {
        quizData,
        answers: detailedAnswers,
        score,
        correctAnswers,
        wrongAnswers,
        unanswered,
        timeTaken,
        isShared,
        shareToken,
        guestName,
      },
    });
  };

  const getAnswerStatus = (index: number) => {
    const answer = answers[index];
    if (answer?.markedForReview) return "review";
    if (answer?.selectedOption !== null) return "answered";
    return "unanswered";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "answered":
        return "bg-success";
      case "review":
        return "bg-warning";
      default:
        return "bg-muted";
    }
  };

  return (
    <div className="min-h-screen bg-background py-3 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="bg-gradient-card backdrop-blur-sm rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-4 md:p-6 shadow-card border border-border/50 mb-3 sm:mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold truncate">{quizData.settings.topic}</h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Question {currentQuestion + 1} of {quizData.questions.length}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 sm:space-x-2 bg-destructive/10 text-destructive px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="font-mono font-semibold text-sm sm:text-base">{formatTime(timeRemaining)}</span>
              </div>
            </div>
          </div>
          <Progress value={progress} className="h-1.5 sm:h-2" />
        </div>

        <div className="grid lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {/* Question Panel */}
          <div className="lg:col-span-3 space-y-3 sm:space-y-4 md:space-y-6">
            {/* Question */}
            <div className="bg-gradient-card backdrop-blur-sm rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-6 lg:p-8 shadow-card border border-border/50">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold leading-relaxed flex-1">
                  {currentQuestion + 1}. {currentQ.question}
                </h3>
              <Button
                  variant={currentAnswer?.markedForReview ? "default" : "outline"}
                  size="sm"
                  onClick={handleMarkForReview}
                  className="rounded-full w-full sm:w-auto shrink-0 hover-scale"
                >
                  <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="text-xs sm:text-sm">{currentAnswer?.markedForReview ? "Marked" : "Mark"}</span>
                </Button>
              </div>

              {/* Options */}
              <div className="space-y-2 sm:space-y-3">
                {currentQ.options.map((option, index) => {
                  const isSelected = currentAnswer?.selectedOption === index;
                  const optionLabel = String.fromCharCode(65 + index);

                  return (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(index)}
                      className={`group w-full text-left p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 ease-in-out ${
                        isSelected
                          ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                          : "border-border hover:border-primary/50 hover:bg-primary/5 hover:shadow-md"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-start space-x-2 sm:space-x-3 flex-1">
                          <div
                            className={`flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-semibold text-xs sm:text-base transition-all duration-300 ${
                              isSelected
                                ? "bg-primary text-primary-foreground scale-110"
                                : "bg-muted text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                            }`}
                          >
                            {optionLabel}
                          </div>
                          <span className="flex-1 pt-0.5 sm:pt-1 text-sm sm:text-base">{option}</span>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0 ml-2 animate-scale-in" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between gap-2 sm:gap-4">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="rounded-full text-xs sm:text-sm px-3 sm:px-4 hover-scale"
              >
                <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </Button>

              {currentQuestion === quizData.questions.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="rounded-full bg-gradient-primary text-xs sm:text-sm px-3 sm:px-4 hover-scale"
                >
                  <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Submit Quiz</span>
                  <span className="sm:hidden">Submit</span>
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="rounded-full bg-gradient-primary text-xs sm:text-sm px-3 sm:px-4 hover-scale"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 sm:ml-1" />
                </Button>
              )}
            </div>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-card backdrop-blur-sm rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-4 md:p-6 shadow-card border border-border/50 lg:sticky lg:top-24">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Questions</h4>
              <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-5 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                {answers.map((_, index) => {
                  const status = getAnswerStatus(index);
                  const isCurrent = index === currentQuestion;

                  return (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestion(index)}
                      className={`aspect-square rounded-md sm:rounded-lg flex items-center justify-center text-xs sm:text-sm font-medium transition-smooth ${
                        isCurrent
                          ? "ring-1 sm:ring-2 ring-primary ring-offset-1 sm:ring-offset-2"
                          : ""
                      } ${getStatusColor(status)} ${
                        status === "answered"
                          ? "text-success-foreground"
                          : status === "review"
                          ? "text-warning-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {index + 1}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-success"></div>
                  <span className="text-muted-foreground">Answered</span>
                </div>
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-warning"></div>
                  <span className="text-muted-foreground">Marked</span>
                </div>
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-muted"></div>
                  <span className="text-muted-foreground">Unanswered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
